import React, { useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import { FormLabel, Offcanvas, Card } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import Creatable from 'react-select/creatable'
import { FileUpload } from 'components/FileUpload'
import { getData } from 'utils/localstorage'
import { useFileUpload } from 'hooks/useFileUpload'
import DatePicker from 'react-datepicker'
import { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import { RichTextField } from 'components/RichText'

import 'react-datepicker/dist/react-datepicker.css'
import './publicaciones.css'

// Constantes
const TIPO_VIDEO = 'video'
const TIPO_GALERIA = 'galeria'

function NuevaPublicacion(props) {
    const { etiquetasExistentes, config, guardar, setQueMostrar } = props
    const [etiquetas, setEtiquetas] = useState([])
    const [paso, setPaso] = useState(1) // 1: seleccionar tipo, 2: llenar datos

    registerLocale('es', es)

    const usuarioActual = getData('usuario')

    const datosInicial = {
        borrador: false,
        tipo: '',
        titulo: '',
        copete: '',
        contenido: '',
        videoUrl: '',
        autor: usuarioActual ? `${usuarioActual.nombre} ${usuarioActual.apellido}` : '',
        fecha: new Date()
    }

    const [datos, setDatos] = useState(datosInicial)
    const [erroresValidacion, setErroresValidacion] = useState({})
    const [videoPreview, setVideoPreview] = useState(null)

    // Hook para imagen destacada (thumbnail)
    const fileUploadThumbnail = useFileUpload({
        maxFiles: 1,
        maxFileSize: 5000000,
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
        endpoint: 'a/multimedia',
        fieldName: 'foto',
        uploadOnSelect: false
    })

    // Hook para galeria (multiples imagenes)
    const fileUploadGaleria = useFileUpload({
        maxFiles: config.maxImagenesGaleria || 10,
        maxFileSize: 5000000,
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
        endpoint: 'a/multimedia',
        fieldName: 'foto',
        uploadOnSelect: false
    })

    const opciones = etiquetasExistentes.map(elem => ({
        label: elem.nombre,
        value: elem.id
    }))

    // Extraer ID de YouTube
    const extraerYouTubeId = (url) => {
        if (!url) return null

        const patterns = [
            /youtube\.com\/watch\?v=([^&]+)/,
            /youtube\.com\/embed\/([^?]+)/,
            /youtu\.be\/([^?]+)/,
            /youtube\.com\/v\/([^?]+)/
        ]

        for (const pattern of patterns) {
            const match = url.match(pattern)
            if (match) return match[1]
        }

        if (url.length === 11) return url
        return null
    }

    // Actualizar preview de video
    const handleVideoUrlChange = (url) => {
        setDatos(prev => ({ ...prev, videoUrl: url }))
        const videoId = extraerYouTubeId(url)
        setVideoPreview(videoId)
    }

    const handleClose = () => {
        setQueMostrar('')
        setDatos(datosInicial)
        setPaso(1)
        fileUploadThumbnail.reset()
        fileUploadGaleria.reset()
    }

    const seleccionarTipo = (tipo) => {
        setDatos(prev => ({ ...prev, tipo }))
        setPaso(2)
    }

    const handleProcesar = async (e) => {
        e.preventDefault()

        if (!validarDatos()) return

        // Subir imagen destacada si existe
        let archivoId = null
        if (fileUploadThumbnail.selectedFiles.length > 0) {
            const resultados = await fileUploadThumbnail.uploadFiles()
            const exitosos = resultados.filter(r => r.success)
            if (exitosos.length > 0) {
                archivoId = exitosos[0].id
            }
        }

        // Subir imagenes de galeria si es tipo galeria
        let imagenesGaleriaIds = []
        if (datos.tipo === TIPO_GALERIA && fileUploadGaleria.selectedFiles.length > 0) {
            const resultados = await fileUploadGaleria.uploadFiles()
            imagenesGaleriaIds = resultados.filter(r => r.success).map(r => r.id)
        }

        // Preparar datos para enviar
        const dataEnviar = {
            ...datos,
            etiquetas: etiquetas.map(elem => elem.label),
            fechaCreacion: new Date()
        }

        if (archivoId) {
            dataEnviar.archivoId = archivoId
        }

        if (imagenesGaleriaIds.length > 0) {
            dataEnviar.imagenesGaleria = {
                nuevas: imagenesGaleriaIds
            }
        }

        guardar(dataEnviar)
        handleClose()
    }

    const validarDatos = () => {
        const nuevosErrores = {}

        if (!datos.titulo || datos.titulo.trim() === '') {
            nuevosErrores.titulo = 'El titulo es requerido'
        }

        if (!datos.autor || datos.autor.trim() === '') {
            nuevosErrores.autor = 'El autor es requerido'
        }

        if (datos.tipo === TIPO_VIDEO && !datos.videoUrl) {
            nuevosErrores.videoUrl = 'La URL del video es requerida'
        }

        if (datos.tipo === TIPO_VIDEO && datos.videoUrl && !extraerYouTubeId(datos.videoUrl)) {
            nuevosErrores.videoUrl = 'URL de YouTube invalida'
        }

        if (datos.tipo === TIPO_GALERIA && fileUploadGaleria.selectedFiles.length === 0) {
            nuevosErrores.galeria = 'Debe agregar al menos una imagen'
        }

        setErroresValidacion(nuevosErrores)
        return Object.keys(nuevosErrores).length === 0
    }

    const handleChange = (value, key) => {
        setDatos(prev => ({ ...prev, [key]: value }))

        if (erroresValidacion[key]) {
            setErroresValidacion(prev => ({ ...prev, [key]: null }))
        }
    }

    const isLoading = fileUploadThumbnail.isLoading || fileUploadGaleria.isLoading

    return (
        <Offcanvas show={true} onHide={handleClose} placement="end" className="publicacion-panel">
            <PanelHeader
                onClick={handleClose}
                iconClass="fa-arrow-left"
                btnText="Atras"
                text={paso === 1 ? 'Nueva Publicacion' : `Nueva Publicacion - ${datos.tipo === TIPO_VIDEO ? 'Video' : 'Galeria'}`}
            />
            <Offcanvas.Body id="nueva-publicacion">
                {/* PASO 1: Seleccionar tipo */}
                {paso === 1 && (
                    <div className="selector-tipo">
                        <h4 className="text-center mb-4">Que tipo de publicacion queres crear?</h4>

                        <Row className="g-4">
                            <Col xs={6}>
                                <Card
                                    className="tipo-card"
                                    onClick={() => seleccionarTipo(TIPO_VIDEO)}
                                >
                                    <Card.Body className="text-center">
                                        <div className="tipo-icon video">
                                            <i className="fa fa-play-circle"></i>
                                        </div>
                                        <h5>Video</h5>
                                        <p className="text-muted small">
                                            Video de YouTube con texto descriptivo
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={6}>
                                <Card
                                    className="tipo-card"
                                    onClick={() => seleccionarTipo(TIPO_GALERIA)}
                                >
                                    <Card.Body className="text-center">
                                        <div className="tipo-icon galeria">
                                            <i className="fa fa-images"></i>
                                        </div>
                                        <h5>Galeria</h5>
                                        <p className="text-muted small">
                                            Hasta {config.maxImagenesGaleria || 10} fotos con texto
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* PASO 2: Formulario segun tipo */}
                {paso === 2 && (
                    <Form onSubmit={handleProcesar}>
                        {/* Boton volver */}
                        <Button
                            variant="link"
                            className="mb-3 p-0"
                            onClick={() => setPaso(1)}
                        >
                            <i className="fa fa-arrow-left me-2"></i>
                            Cambiar tipo
                        </Button>

                        {/* Estado */}
                        <Card className="mb-3">
                            <Card.Body>
                                <Form.Group controlId="pub-borrador">
                                    <Form.Check
                                        type="switch"
                                        label={datos.borrador ? 'Borrador' : 'Publicada'}
                                        checked={!datos.borrador}
                                        onChange={(e) => handleChange(!e.target.checked, 'borrador')}
                                    />
                                    <Form.Text className="text-muted">
                                        Los borradores no son visibles para los usuarios
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        {/* Datos basicos */}
                        <Card className="mb-3">
                            <Card.Header>
                                <i className="fa fa-edit me-2"></i>
                                Informacion General
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3" controlId="pub-titulo">
                                    <FormLabel>Titulo <span className="text-danger">*</span></FormLabel>
                                    <Form.Control
                                        value={datos.titulo}
                                        onChange={(e) => handleChange(e.target.value, 'titulo')}
                                        isInvalid={!!erroresValidacion.titulo}
                                        placeholder="Titulo de la publicacion"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {erroresValidacion.titulo}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="pub-etiquetas">
                                    <FormLabel>Etiquetas</FormLabel>
                                    <Creatable
                                        onChange={setEtiquetas}
                                        options={opciones}
                                        isMulti={true}
                                        placeholder="Seleccionar o crear etiquetas..."
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="pub-copete">
                                    <FormLabel>Copete / Resumen</FormLabel>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={datos.copete}
                                        onChange={(e) => handleChange(e.target.value, 'copete')}
                                        placeholder="Breve descripcion que aparece en el listado"
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="pub-autor">
                                            <FormLabel>Autor <span className="text-danger">*</span></FormLabel>
                                            <Form.Control
                                                value={datos.autor}
                                                onChange={(e) => handleChange(e.target.value, 'autor')}
                                                isInvalid={!!erroresValidacion.autor}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {erroresValidacion.autor}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="pub-fecha">
                                            <FormLabel>Fecha</FormLabel>
                                            <DatePicker
                                                selected={datos.fecha}
                                                onChange={(fecha) => handleChange(fecha, 'fecha')}
                                                showTimeSelect
                                                locale="es"
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                className="form-control"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Contenido especifico segun tipo */}
                        {datos.tipo === TIPO_VIDEO && (
                            <Card className="mb-3">
                                <Card.Header>
                                    <i className="fa fa-play-circle me-2"></i>
                                    Video de YouTube
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-3" controlId="pub-videoUrl">
                                        <FormLabel>URL del Video <span className="text-danger">*</span></FormLabel>
                                        <Form.Control
                                            value={datos.videoUrl}
                                            onChange={(e) => handleVideoUrlChange(e.target.value)}
                                            isInvalid={!!erroresValidacion.videoUrl}
                                            placeholder="https://www.youtube.com/watch?v=..."
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {erroresValidacion.videoUrl}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            Pega la URL completa del video de YouTube
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Preview del video */}
                                    {videoPreview && (
                                        <div className="video-preview mb-3">
                                            <div className="ratio ratio-16x9">
                                                <iframe
                                                    src={`https://www.youtube.com/embed/${videoPreview}`}
                                                    title="Preview"
                                                    allowFullScreen
                                                ></iframe>
                                            </div>
                                        </div>
                                    )}

                                    {/* Thumbnail opcional */}
                                    <div className="mt-3">
                                        <FormLabel>Imagen destacada (opcional)</FormLabel>
                                        <Form.Text className="text-muted d-block mb-2">
                                            Si no se sube, se usara el thumbnail del video
                                        </Form.Text>
                                        <FileUpload
                                            title=""
                                            accept=".png, .jpg, .jpeg, .webp"
                                            buttonText="Elegir"
                                            buttonTextSingular="imagen"
                                            updateFiles={fileUploadThumbnail.handleSelectFiles}
                                            showPreview={true}
                                            previewTitle="Imagen seleccionada"
                                        />
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        {datos.tipo === TIPO_GALERIA && (
                            <Card className="mb-3">
                                <Card.Header>
                                    <i className="fa fa-images me-2"></i>
                                    Galeria de Imagenes
                                </Card.Header>
                                <Card.Body>
                                    {erroresValidacion.galeria && (
                                        <Alert variant="danger" className="mb-3">
                                            {erroresValidacion.galeria}
                                        </Alert>
                                    )}

                                    <FileUpload
                                        title={`Imagenes (max. ${config.maxImagenesGaleria || 10})`}
                                        accept=".png, .jpg, .jpeg, .webp"
                                        buttonText="Elegir"
                                        buttonTextSingular="imagenes"
                                        multiple={true}
                                        updateFiles={fileUploadGaleria.handleSelectFiles}
                                        showPreview={true}
                                        previewTitle="Imagenes seleccionadas"
                                    />

                                    <Form.Text className="text-muted">
                                        Podes seleccionar hasta {config.maxImagenesGaleria || 10} imagenes
                                    </Form.Text>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Contenido de texto */}
                        <Card className="mb-3">
                            <Card.Header>
                                <i className="fa fa-align-left me-2"></i>
                                Contenido
                            </Card.Header>
                            <Card.Body>
                                <RichTextField
                                    value={datos.contenido}
                                    onChange={(contenido) => handleChange(contenido, 'contenido')}
                                    label=""
                                />
                            </Card.Body>
                        </Card>
                    </Form>
                )}
            </Offcanvas.Body>

            {paso === 2 && (
                <PanelFooter>
                    <Button
                        variant="outline-secondary"
                        onClick={handleClose}
                        className="me-2"
                        disabled={isLoading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleProcesar}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <><i className="fa fa-spinner fa-spin me-2"></i> Guardando...</>
                        ) : (
                            <><i className="fa fa-save me-2"></i> Guardar</>
                        )}
                    </Button>
                </PanelFooter>
            )}
        </Offcanvas>
    )
}

export default NuevaPublicacion
